import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { useState } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts'

import Badge from '../../atoms/badge'
import Typography, { typography } from '../../atoms/typography'
import ChartContainer from './chart-container'
import ChartTooltipContent, { ChartTooltip } from './chart-tooltip-content'
import { GraphData } from './utils'

interface BarGraphProps {
  /**
   * title of the graph
   */
  title: string

  /**
   * description of the graph, small sized text under the title of the graph
   */
  description?: string

  /**
   * color of the bars
   */
  color?: string

  /**
   * data to be displayed in the graph
   */
  data: GraphData[]

  /**
   * X axis label
   */
  xAxisLabel?: string

  /**
   * Y axis label
   */
  yAxisLabel?: string

  /**
   * radius of the bars
   */
  barRadius?: number

  /**
   * size of the bars
   */
  barSize?: number

  /**
   *  x-axis divided
   */
  tickLine?: boolean

  /**
   * className for the bar container className
   */
  className?: string

  /**
   * show dividers
   * */
  showXAxisLine?: boolean

  /**
   * show y values
   *  */
  showYAxisValue?: boolean

  hasborder?: boolean
}

// Minimal subset of Recharts tick props we rely upon.
type CustomTickProps = {
  x?: number
  y?: number
  payload?: {
    value?: string | number
    [key: string]: unknown
  }
}

const CustomTick = ({ x = 0, y = 0, payload }: CustomTickProps) => {
  const rawValue = payload?.value
  const text = rawValue !== undefined ? String(rawValue) : ''

  // Split at natural break points
  const breakText = (str: string) => {
    if (str.length <= 20) return [str]

    // Then try spaces
    const words = str.split(' ')
    if (words.length <= 2) return [str]

    const mid = Math.floor(words.length / 2)
    return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')]
  }

  const lines = breakText(text)

  return (
    <g transform={`translate(${x},${y + 10})`}>
      {lines.map((line, index) => (
        <text
          key={index}
          x={0}
          y={index * 14}
          textAnchor="middle"
          fill="#666"
          fontSize="11"
          style={typography['small-medium']}
        >
          {line}
        </text>
      ))}
    </g>
  )
}

const BarGraph = ({
  title,
  description,
  data,
  barRadius = 20,
  barSize = 60,
  tickLine = false,
  showXAxisLine = true,
  showYAxisValue = true,
  hasborder = true,
  color = tailwindColors.primary.dark.DEFAULT,
  xAxisLabel,
  yAxisLabel,
  className
}: BarGraphProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  return (
    <div
      className={cn('flex gap-7 flex-col p-5 border border-grey-light rounded-lg', {
        'border-none p-0': !hasborder
      })}
    >
      {/* Title and description - only show if title exists */}
      {title && (
        <div className={'flex flex-col gap-1'}>
          <Typography variant={'tag'}>{title}</Typography>
          {description && (
            <Typography variant={'label'} color={tailwindColors.grey.light.active}>
              {description}
            </Typography>
          )}
        </div>
      )}

      {/* Chart container with axis labels */}
      <div className="relative w-full">
        {/* Y axis label - positioned absolutely on the left */}
        {yAxisLabel && (
          <div className="absolute left-0 top-1/2 z-10 flex w-10 -translate-y-16 justify-center">
            <div className="transform -rotate-90 whitespace-nowrap">
              <Badge text={yAxisLabel} variant={'warning'} textVariant={'tag'} />
            </div>
          </div>
        )}

        {/* Chart container with proper margins */}
        <div className="flex flex-col gap-4" style={{ marginLeft: yAxisLabel ? '40px' : '0' }}>
          {/* Chart */}
          <ChartContainer config={{}} className={cn('w-full h-96', className)}>
            <BarChart accessibilityLayer data={data}>
              {showXAxisLine && <CartesianGrid vertical={false} />}

              <XAxis
                dataKey={'key'}
                tickLine={tickLine}
                tickMargin={10}
                onMouseLeave={() => setActiveIndex(null)} // reset on leave
                axisLine={false}
                style={typography['small-medium']}
                tickFormatter={(value: string) => StringFormatter.toTitleCase(value)}
                interval={0}
                tick={CustomTick}
                height={80} // Increase height for wrapped text
              />
              {showYAxisValue && <YAxis dataKey={'value'} tickLine={false} axisLine={false} />}

              <ChartTooltip
                cursor={{ fill: 'transparent', stroke: 'transparent' }}
                content={<ChartTooltipContent data={data} />}
              />

              <Bar
                dataKey={'value'}
                radius={barRadius}
                barSize={barSize}
                style={{
                  filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.1))',
                  transition: 'fill 0.2s ease-in-out'
                }}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={activeIndex === index ? entry.hoverFill : entry.fill || color}
                    onMouseEnter={() => setActiveIndex(index)} // set active index on hover
                    onMouseLeave={() => setActiveIndex(null)} // reset on leave
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>

          {/* X axis label - positioned below chart */}
          {xAxisLabel && (
            <div className="flex justify-center -mt-2">
              <Badge text={xAxisLabel} variant={'warning'} textVariant={'caption'} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BarGraph
